/**
 * Connector logo registry — React adapter layer.
 *
 * This file does NOT own any connector data. All metadata (name, iconBg,
 * svgPath, copy) lives in src/tools/connectors/<id>.js.
 *
 * This file converts those plain-JS connector modules into React logo
 * components that can be dropped anywhere in the UI.
 *
 * To add a new connector logo:
 *   1. Add svgPath (and optionally svgViewBox, svgFillRule) to the connector module.
 *   2. Import it below and add one line to CONNECTOR_LOGOS.
 *
 * Connectors with svgPath: null are not registered here — the UI falls back
 * to the iconBg colour from the API with no icon rendered.
 */
import React from 'react';
import notionConnector      from '../tools/connectors/notion';
import googleDriveConnector from '../tools/connectors/google-drive';
import amplitudeConnector   from '../tools/connectors/amplitude';
import figmaConnector       from '../tools/connectors/figma';
import miroConnector        from '../tools/connectors/miro';
import askableConnector     from '../tools/connectors/askable';
import ideallyConnector     from '../tools/connectors/ideally';
import granolaConnector     from '../tools/connectors/granola';
import githubConnector      from '../tools/connectors/github';

function makeLogo(connector) {
  if (!connector.svgPath && !connector.svgPaths?.length) return null;
  const Logo = ({ size = 16 }) => (
    <svg
      width={size}
      height={size}
      viewBox={connector.svgViewBox || '0 0 24 24'}
      fillRule={connector.svgFillRule || 'nonzero'}
      xmlns="http://www.w3.org/2000/svg"
    >
      {connector.svgPaths?.length
        ? connector.svgPaths.map((path, index) => (
            <path
              key={index}
              d={path.d}
              fill={path.fill}
              fillRule={path.fillRule || 'nonzero'}
              stroke={path.stroke}
              strokeWidth={path.strokeWidth}
              strokeLinecap={path.strokeLinecap}
              strokeLinejoin={path.strokeLinejoin}
            />
          ))
        : <path d={connector.svgPath} fill="white" fillRule={connector.svgFillRule || 'nonzero'} />}
    </svg>
  );
  return { bg: connector.iconBg, Logo };
}

const allConnectors = [
  notionConnector,
  googleDriveConnector,
  amplitudeConnector,
  figmaConnector,
  miroConnector,
  askableConnector,
  ideallyConnector,
  granolaConnector,
  githubConnector,
];

export const CONNECTOR_LOGOS = Object.fromEntries(
  allConnectors
    .map(c => [c.id, makeLogo(c)])
    .filter(([, logo]) => logo !== null)
);

export function getConnectorLogo(id) {
  return CONNECTOR_LOGOS[id] || null;
}
